import Vue from 'vue'
import DataEventEnum from '@/app-core/lib/services/enum/DataEventEnum'
import OSTagEnum from '@/app-core/lib/services/enum/OSTagEnum'
import DataEventService from '@/app-core/lib/services/event/DataEventService'
import i18n from '@/lang/lang'
import {CapacitorService} from '@/app-core/lib/services/Capacitor'
import AccountSettingsModel from '@/app-core/api/models/account/AccountSettingsModel'
import API from '@/app-core/api/Api'
import CustomerModel from '@/app-core/api/models/customer/CustomerModel'
import {clone} from '@/app-core/lib/utils/helper'
import {EventBus} from '@/app-core/lib/events/eventBus'

let loaded = false

const Tier = {
  Free: {
    TagsTotal: 2,
    Tags     : [
      OSTagEnum.NOTIFICATIONS_SYSTEM,
      OSTagEnum.NOTIFICATIONS_MARKETING
    ]
  },
  Growth: {
    TagsTotal: 10,
    Tags     : [
      OSTagEnum.LAST_ORDER,
      OSTagEnum.USER_TYPE,
      OSTagEnum.CART_UPDATE,
      OSTagEnum.CART_ITEMS,
      OSTagEnum.AMOUNT_SPENT,
      OSTagEnum.FIRST_NAME,
      OSTagEnum.USER_LOCALE,
      OSTagEnum.BIRTHDAY,
      OSTagEnum.LOYALTY_POINTS
    ]
  },
  Professional: {
    TagsTotal: 20,
    Tags     : [
      OSTagEnum.LAST_ORDER,
      OSTagEnum.USER_TYPE,
      OSTagEnum.CART_UPDATE,
      OSTagEnum.CART_ITEMS,
      OSTagEnum.AMOUNT_SPENT,
      OSTagEnum.FIRST_NAME,
      OSTagEnum.USER_LOCALE,
      OSTagEnum.BIRTHDAY,
      OSTagEnum.LOYALTY_POINTS
    ]
  }
}

export const setupOneSignal = (Settings) => {
  if (loaded) return
  loaded = true

  try {
    const IS_DEV = import.meta.env.MODE === 'development'
    const APP_ID = IS_DEV && import.meta.env.VITE_ONESIGNAL_APP_ID ? import.meta.env.VITE_ONESIGNAL_APP_ID : Settings?.OneSignal?.AppId || ''
    const ACTIVE = Settings?.OneSignal?.Active || false
    const EMAIL_ACTIVE = ACTIVE && Settings?.OneSignal?.Email?.Active || false
    const SMS_ACTIVE = ACTIVE && Settings?.OneSignal?.Sms?.Active || false
    const PUSH_ACTIVE = ACTIVE && Settings?.OneSignal?.Push?.Active || false
    const TIER = ACTIVE && Settings?.OneSignal?.Tier || 'Free'
    const MOBILE_ACTIVE = ACTIVE && PUSH_ACTIVE && CapacitorService.isNative && Settings?.OneSignal?.Push?.Mobile?.Active || false
    const WEB_ACTIVE = ACTIVE && PUSH_ACTIVE && !CapacitorService.isNative && Settings?.OneSignal?.Push?.Web?.Active || false
    const SEND_DATA = ACTIVE && APP_ID && (MOBILE_ACTIVE || WEB_ACTIVE || EMAIL_ACTIVE || SMS_ACTIVE)
    const vuetifyTheme = window?.Vue?.$vuetify?.theme?.dark ? window?.Vue?.$vuetify?.theme?.themes?.dark : window?.Vue?.$vuetify?.theme?.themes?.light

    window.$OneSignal = {
      Service : () => SEND_DATA ? (window?.plugins?.OneSignal || window?.OneSignal || window?.OneSignalDeferred) || [] : [],
      Version : null,
      Settings: {
        AppId : '',
        Tier  : TIER,
        Active: false,
        Email : {
          Active: false
        },
        Sms: {
          Active: false
        },
        Push: {
          Active: false,
          Mobile: {
            Active: false
          },
          Web: {
            Active                      : false,
            appId                       : '',
            allowLocalhostAsSecureOrigin: IS_DEV,
            autoResubscribe             : true,
            persistNotification         : true,
            requiresUserPrivacyConsent  : false,
            serviceWorkerParam          : {scope: '/push/onesignal/'},
            serviceWorkerPath           : 'push/onesignal/OneSignalSDKWorker.js',
            serviceWorkerUpdaterPath    : 'push/onesignal/OneSignalSDKUpdaterWorker.js',
            notifyButton                : {
              enable    : false,
              showCredit: false,
              size      : 'medium',
              position  : 'bottom-right',
              offset    : {
                left  : '5px',
                right : '5px',
                bottom: '5px'
              },
              colors: {
                'circle.background'                : vuetifyTheme['brand-primary']?.base || '',
                'circle.foreground'                : '#FFFFFF',
                'badge.background'                 : vuetifyTheme['brand-primary']?.base || '',
                'badge.foreground'                 : '#FFFFFF',
                'badge.bordercolor'                : vuetifyTheme['brand-primary']?.base || '',
                'pulse.color'                      : vuetifyTheme['brand-primary']?.base || '',
                'dialog.button.background.hovering': vuetifyTheme['brand-primary']?.base || '',
                'dialog.button.background.active'  : vuetifyTheme['brand-primary']?.base || '',
                'dialog.button.background'         : vuetifyTheme['brand-primary']?.base || '',
                'dialog.button.foreground'         : '#FFFFFF'
              },
              text: {
                'tip.state.unsubscribed'        : i18n.t('Common.PushNotifications.notifyButton.tip.state.unsubscribed').replace(/'/g, '\\\''),
                'tip.state.subscribed'          : i18n.t('Common.PushNotifications.notifyButton.tip.state.subscribed').replace(/'/g, '\\\''),
                'tip.state.blocked'             : i18n.t('Common.PushNotifications.notifyButton.tip.state.blocked').replace(/'/g, '\\\''),
                'message.prenotify'             : i18n.t('Common.PushNotifications.notifyButton.message.prenotify').replace(/'/g, '\\\''),
                'message.action.subscribed'     : i18n.t('Common.PushNotifications.notifyButton.message.action.subscribed').replace(/'/g, '\\\''),
                'message.action.resubscribed'   : i18n.t('Common.PushNotifications.notifyButton.message.action.resubscribed').replace(/'/g, '\\\''),
                'message.action.unsubscribed'   : i18n.t('Common.PushNotifications.notifyButton.message.action.unsubscribed').replace(/'/g, '\\\''),
                'dialog.main.title'             : i18n.t('Common.PushNotifications.notifyButton.dialog.main.title').replace(/'/g, '\\\''),
                'dialog.main.button.subscribe'  : i18n.t('Common.PushNotifications.notifyButton.dialog.main.button.subscribe').replace(/'/g, '\\\''),
                'dialog.main.button.unsubscribe': i18n.t('Common.PushNotifications.notifyButton.dialog.main.button.unsubscribe').replace(/'/g, '\\\''),
                'dialog.blocked.title'          : i18n.t('Common.PushNotifications.notifyButton.dialog.blocked.title').replace(/'/g, '\\\''),
                'dialog.blocked.message'        : i18n.t('Common.PushNotifications.notifyButton.dialog.blocked.message').replace(/'/g, '\\\'')
              }
            },
            promptOptions: {
              slidedown: {
                prompts: [
                  {
                    type      : 'category',
                    autoPrompt: true,
                    text      : {
                      actionMessage       : i18n.t('Common.PushNotifications.promptOptions.actionMessage').replace(/'/g, '\\\''),
                      acceptButton        : i18n.t('Common.PushNotifications.promptOptions.acceptButton').replace(/'/g, '\\\''),
                      cancelButton        : i18n.t('Common.PushNotifications.promptOptions.cancelButton').replace(/'/g, '\\\''),
                      emailLabel          : i18n.t('Common.PushNotifications.promptOptions.emailLabel').replace(/'/g, '\\\''),
                      smsLabel            : i18n.t('Common.PushNotifications.promptOptions.smsLabel').replace(/'/g, '\\\''),
                      updateMessage       : i18n.t('Common.PushNotifications.promptOptions.updateMessage').replace(/'/g, '\\\''),
                      negativeUpdateButton: i18n.t('Common.PushNotifications.promptOptions.negativeUpdateButton').replace(/'/g, '\\\''),
                      positiveUpdateButton: i18n.t('Common.PushNotifications.promptOptions.positiveUpdateButton').replace(/'/g, '\\\''),
                      confirmMessage      : i18n.t('Common.PushNotifications.promptOptions.confirmMessage').replace(/'/g, '\\\'')
                    },
                    delay: {
                      pageViews: 1,
                      timeDelay: 10
                    },
                    categories: [
                      {tag: 'system', label: i18n.t('Common.PushNotifications.promptOptions.category.system').replace(/'/g, '\\\'')},
                      {tag: 'marketing', label: i18n.t('Common.PushNotifications.promptOptions.category.marketing').replace(/'/g, '\\\'')}
                    ]
                  },
                  {
                    type      : 'push',
                    autoPrompt: false,
                    text      : {
                      actionMessage       : i18n.t('Common.PushNotifications.promptOptions.actionMessage').replace(/'/g, '\\\''),
                      acceptButton        : i18n.t('Common.PushNotifications.promptOptions.acceptButton').replace(/'/g, '\\\''),
                      cancelButton        : i18n.t('Common.PushNotifications.promptOptions.cancelButton').replace(/'/g, '\\\''),
                      emailLabel          : i18n.t('Common.PushNotifications.promptOptions.emailLabel').replace(/'/g, '\\\''),
                      smsLabel            : i18n.t('Common.PushNotifications.promptOptions.smsLabel').replace(/'/g, '\\\''),
                      updateMessage       : i18n.t('Common.PushNotifications.promptOptions.updateMessage').replace(/'/g, '\\\''),
                      negativeUpdateButton: i18n.t('Common.PushNotifications.promptOptions.negativeUpdateButton').replace(/'/g, '\\\''),
                      positiveUpdateButton: i18n.t('Common.PushNotifications.promptOptions.positiveUpdateButton').replace(/'/g, '\\\''),
                      confirmMessage      : i18n.t('Common.PushNotifications.promptOptions.confirmMessage').replace(/'/g, '\\\'')
                    },
                    delay: {
                      pageViews: 1,
                      timeDelay: 10
                    }
                  },
                  {
                    type      : 'sms',
                    autoPrompt: false,
                    text      : {
                      actionMessage       : i18n.t('Common.PushNotifications.promptOptions.actionMessage').replace(/'/g, '\\\''),
                      acceptButton        : i18n.t('Common.PushNotifications.promptOptions.acceptButton').replace(/'/g, '\\\''),
                      cancelButton        : i18n.t('Common.PushNotifications.promptOptions.cancelButton').replace(/'/g, '\\\''),
                      emailLabel          : i18n.t('Common.PushNotifications.promptOptions.emailLabel').replace(/'/g, '\\\''),
                      smsLabel            : i18n.t('Common.PushNotifications.promptOptions.smsLabel').replace(/'/g, '\\\''),
                      updateMessage       : i18n.t('Common.PushNotifications.promptOptions.updateMessage').replace(/'/g, '\\\''),
                      negativeUpdateButton: i18n.t('Common.PushNotifications.promptOptions.negativeUpdateButton').replace(/'/g, '\\\''),
                      positiveUpdateButton: i18n.t('Common.PushNotifications.promptOptions.positiveUpdateButton').replace(/'/g, '\\\''),
                      confirmMessage      : i18n.t('Common.PushNotifications.promptOptions.confirmMessage').replace(/'/g, '\\\'')
                    },
                    delay: {
                      pageViews: 1,
                      timeDelay: 10
                    }
                  },
                  {
                    type      : 'email',
                    autoPrompt: false,
                    text      : {
                      actionMessage       : i18n.t('Common.PushNotifications.promptOptions.actionMessage').replace(/'/g, '\\\''),
                      acceptButton        : i18n.t('Common.PushNotifications.promptOptions.acceptButton').replace(/'/g, '\\\''),
                      cancelButton        : i18n.t('Common.PushNotifications.promptOptions.cancelButton').replace(/'/g, '\\\''),
                      emailLabel          : i18n.t('Common.PushNotifications.promptOptions.emailLabel').replace(/'/g, '\\\''),
                      smsLabel            : i18n.t('Common.PushNotifications.promptOptions.smsLabel').replace(/'/g, '\\\''),
                      updateMessage       : i18n.t('Common.PushNotifications.promptOptions.updateMessage').replace(/'/g, '\\\''),
                      negativeUpdateButton: i18n.t('Common.PushNotifications.promptOptions.negativeUpdateButton').replace(/'/g, '\\\''),
                      positiveUpdateButton: i18n.t('Common.PushNotifications.promptOptions.positiveUpdateButton').replace(/'/g, '\\\''),
                      confirmMessage      : i18n.t('Common.PushNotifications.promptOptions.confirmMessage').replace(/'/g, '\\\'')
                    },
                    delay: {
                      pageViews: 1,
                      timeDelay: 10
                    }
                  },
                  {
                    type      : 'smsAndEmail',
                    autoPrompt: false,
                    text      : {
                      actionMessage       : i18n.t('Common.PushNotifications.promptOptions.actionMessage').replace(/'/g, '\\\''),
                      acceptButton        : i18n.t('Common.PushNotifications.promptOptions.acceptButton').replace(/'/g, '\\\''),
                      cancelButton        : i18n.t('Common.PushNotifications.promptOptions.cancelButton').replace(/'/g, '\\\''),
                      emailLabel          : i18n.t('Common.PushNotifications.promptOptions.emailLabel').replace(/'/g, '\\\''),
                      smsLabel            : i18n.t('Common.PushNotifications.promptOptions.smsLabel').replace(/'/g, '\\\''),
                      updateMessage       : i18n.t('Common.PushNotifications.promptOptions.updateMessage').replace(/'/g, '\\\''),
                      negativeUpdateButton: i18n.t('Common.PushNotifications.promptOptions.negativeUpdateButton').replace(/'/g, '\\\''),
                      positiveUpdateButton: i18n.t('Common.PushNotifications.promptOptions.positiveUpdateButton').replace(/'/g, '\\\''),
                      confirmMessage      : i18n.t('Common.PushNotifications.promptOptions.confirmMessage').replace(/'/g, '\\\'')
                    },
                    delay: {
                      pageViews: 1,
                      timeDelay: 10
                    }
                  }
                ]
              }
            },
            welcomeNotification: {
              disable: true,
              title  : `Welcome!`,
              message: i18n.t('Common.PushNotifications.welcomeNotification.message').replace(/'/g, '\\\'')
            }
          }
        }
      }
    }

    window.$OneSignal.Settings.AppId = APP_ID
    window.$OneSignal.Settings.Tier = TIER
    window.$OneSignal.Settings.SendData = SEND_DATA
    window.$OneSignal.Settings.Active = ACTIVE
    window.$OneSignal.Settings.Email.Active = EMAIL_ACTIVE
    window.$OneSignal.Settings.Sms.Active = SMS_ACTIVE
    window.$OneSignal.Settings.Push.Active = PUSH_ACTIVE
    window.$OneSignal.Settings.Push.Mobile.Active = MOBILE_ACTIVE
    window.$OneSignal.Settings.Push.Web.Active = WEB_ACTIVE
    window.$OneSignal.Settings.Push.Web.appId = APP_ID
    window.$OneSignal.Settings.Push.Web.requiresUserPrivacyConsent = true

    // console.log('OneSignal::', window.$OneSignal)
    // console.log('>>> OneSignal APP_ID::', APP_ID)
    // console.log('>>> OneSignal ACTIVE::', ACTIVE)

    if (!ACTIVE || !APP_ID) return

    // console.log('>>> OneSignal MOBILE_ACTIVE::', MOBILE_ACTIVE)
    // console.log('>>> OneSignal WEB_ACTIVE::', WEB_ACTIVE)

    Object.defineProperties(Vue.prototype, {
      $OneSignal: {
        get: function () {
          return window.$OneSignal
        }
      }
    })

    if (MOBILE_ACTIVE) {
      const OneSignal = window?.$OneSignal?.Service()
      if (!OneSignal) return

      const notificationOpenedCallback = function (jsonData) {

        console.log('notificationOpenedCallback: ' + JSON.stringify(jsonData))
      }

      // Set iOS Settings
      const iosSettings = {}
      iosSettings.kOSSettingsKeyAutoPrompt = false
      iosSettings.kOSSettingsKeyInAppLaunchURL = false

      const isOneSignalv3 = typeof OneSignal.setAppId === 'function'
      const isOneSignalv5 = typeof OneSignal.initialize === 'function'

      if (isOneSignalv5) {
        window.$OneSignal.Version = 5
        // 0 = None, 1 = Fatal, 2 = Errors, 3 = Warnings, 4 = Info, 5 = Debug, 6 = Verbose
        OneSignal.Debug.setLogLevel(0)
        OneSignal.initialize(window.$OneSignal.Settings.AppId)
      } else if (isOneSignalv3) {
        window.$OneSignal.Version = 3
        // 0 = None, 1 = Fatal, 2 = Errors, 3 = Warnings, 4 = Info, 5 = Debug, 6 = Verbose
        // OneSignal.setLogLevel({ logLevel   : 6, visualLevel: 0 })
        OneSignal.setAppId(window.$OneSignal.Settings.AppId)
        OneSignal.setNotificationOpenedHandler(notificationOpenedCallback)
      } else {
        window.$OneSignal.Version = 1
        OneSignal
          .startInit(window.$OneSignal.Settings.AppId)
          .handleNotificationOpened(notificationOpenedCallback)
          .iOSSettings(iosSettings)
          .inFocusDisplaying(OneSignal.OSInFocusDisplayOption.Notification)
          .endInit()
      }

      if (isOneSignalv5) {
        OneSignal.Notifications.requestPermission(false).then((accepted) => {

          console.log('User accepted notifications: ' + JSON.stringify(accepted))
        })

        window.afterOneSignalInit()
      } else {
        // The promptForPushNotificationsWithUserResponse function will show the iOS push notification prompt.
        OneSignal.promptForPushNotificationsWithUserResponse(function (accepted) {

          console.log('User accepted notifications: ' + accepted)
        })

        OneSignal.addSubscriptionObserver(state => {
          DataEventService.Emit(DataEventEnum.EVENT, {
            Event  : DataEventEnum.PUSH_SUBSCRIPTION_CHANGE,
            Payload: (!state.from.subscribed && state.to.subscribed)
          })
        })
      }
    }

    if (WEB_ACTIVE) {
      const OneSignalSDK = document.createElement('script')
      OneSignalSDK.setAttribute('src', 'https://cdn.onesignal.com/sdks/web/v16/OneSignalSDK.page.js')
      OneSignalSDK.setAttribute('defer', '')
      document.head.appendChild(OneSignalSDK)

      const title = i18n.t('Common.App.Title').replace(/'/g, '\\\'')
      const url = window?.location?.origin || ''

      const OneSignalScriptText = `
        var OneSignalDeferred = window?.$OneSignal?.Service()
        var initConfig = window.$OneSignal.Settings.Push.Web

        OneSignalDeferred.push(async function (OneSignal) {
          OneSignal.Notifications.setDefaultTitle('${title}')
          OneSignal.Notifications.setDefaultUrl('${url}')


          OneSignal.Notifications.addEventListener("foregroundWillDisplay", function (event) {
            window.EventBus.$emit('data-event', { Event: 'data-event:push_notification_foreground_will_display', Payload: event })
          })
          OneSignal.Notifications.addEventListener("dismiss", function (event) {
            window.EventBus.$emit('data-event', { Event: 'data-event:push_notification_dismiss', Payload: event })
          })
          OneSignal.Notifications.addEventListener("permissionChange", function (event) {
            window.EventBus.$emit('data-event', { Event: 'data-event:push_notification_permission_change', Payload: event })
          })
          OneSignal.Notifications.addEventListener("permissionPromptDisplay", function (event) {
            window.EventBus.$emit('data-event', { Event: 'data-event:push_notification_permission_prompt_display', Payload: event })
          })
          OneSignal.Notifications.addEventListener("click", function (event) {
            window.EventBus.$emit('data-event', { Event: 'data-event:push_notification_click', Payload: event })
          })
          OneSignal.Slidedown.addEventListener("slidedownShown", function (event) {
            window.EventBus.$emit('data-event', { Event: 'data-event:push_slide_down_shown', Payload: event })
          })
          OneSignal.Slidedown.addEventListener("slidedownAllowClick", function (event, data) {
            window.EventBus.$emit('data-event', { Event: 'data-event:push_slide_down_allow', Payload: event })
          })
          OneSignal.Slidedown.addEventListener("slidedownCancelClick", function (event) {
            window.EventBus.$emit('data-event', { Event: 'data-event:push_slide_down_cancel', Payload: event })
          })
          OneSignal.User.PushSubscription.addEventListener("change", function (event) {
            window.EventBus.$emit('data-event', { Event: 'data-event:push_user_subscription_change', Payload: event })
          })
          OneSignal.User.addEventListener("change", function (event) {
            window.EventBus.$emit('data-event', { Event: 'data-event:push_user_state_change', Payload: event })
          })

          await OneSignal.init(initConfig)
          await OneSignal.setConsentGiven(true)

          window.afterOneSignalInit()
        });
      `

      const OneSignalScript = document.createElement('script')
      OneSignalScript.textContent = OneSignalScriptText || ''
      document.head.appendChild(OneSignalScript)
    }
  } catch (e) {
    console.error(e)
  }
}

window.afterOneSignalInit = () => {
  DataEventService.On(DataEventEnum.EVENT, handleOSDataEvent)

  const OneSignal = window?.$OneSignal?.Service()
  // OneSignal.Debug.setLogLevel('debug')
  OneSignal.User.setLanguage(i18n?.locale || 'en')

  if (window?.Vue) {
    DataEventService.Emit(DataEventEnum.EVENT, {
      Event  : DataEventEnum.INIT,
      Payload: {
        Locale: window?.Vue?.$i18n?.locale || 'en',
        Info  : window?.Vue?.user?.Info || null,
        Method: window?.Vue?.user?.Info ? window?.Vue?.user?.Info?.SocialProvider || 'Email' : 'Guest'
      }
    })
  }
}

/**
 * Applies the consent state. Called from applyConsent() in App.vue.
 * @param {boolean} granted
 * @param {Object} settings
 */
export async function applyOneSignalConsent(granted, settings) {
  const OneSignal = window?.$OneSignal?.Service()

  if (granted) {
    if (!loaded) {
      setupOneSignal(settings)
    } else {
      OneSignal.setConsentGiven(true)
    }
    return
  }

  // Revoked: stop sending data and opt the push subscription out
  if (loaded) {
    await OneSignal.User.PushSubscription.optOut()
    await OneSignal.setConsentGiven(false)
  }
  clearOneSignalStorage()
}

/**
 * OneSignal does NOT rely mainly on cookies but on IndexedDB and localStorage,
 * which CookieConsent's autoClear does not touch. We clear them ourselves.
 */
export function clearOneSignalStorage() {
  try {
    Object.keys(localStorage)
      .filter((k) => /onesignal/i.test(k))
      .forEach((k) => localStorage.removeItem(k))
  } catch (e) { /* storage blocked */
  }

  try {
    if (indexedDB.databases) {
      indexedDB.databases().then((dbs) => {
        dbs
          .filter((db) => db.name && /onesignal/i.test(db.name))
          .forEach((db) => indexedDB.deleteDatabase(db.name))
      })
    } else {
      indexedDB.deleteDatabase('ONE_SIGNAL_SDK_DB')
    }
  } catch (e) { /* IndexedDB unavailable */
  }
}


const handleOSDataEvent = ($data) => {
  // console.log('handleAnalyticsEvent:', $data)
  const OneSignal = window?.$OneSignal?.Service()
  const Data = JSON.parse(JSON.stringify($data))
  const Settings = window.$OneSignal.Settings
  const Timestamp = Math.floor(Date.now() / 1000)

  if (!Settings.SendData) return

  switch (Data.Event) {
  case DataEventEnum.INIT:
    CapacitorService.isNative ? onInit(Data, Timestamp) : OneSignal.push(() => onInit(Data, Timestamp))
    break

  case DataEventEnum.LOCALE:
    CapacitorService.isNative ? onLocale(Data, Timestamp) : OneSignal.push(() => onLocale(Data, Timestamp))
    break

  case DataEventEnum.SIGN_UP:
  case DataEventEnum.LOGIN:
    CapacitorService.isNative ? onLogin(Data, Timestamp) : OneSignal.push(() => onLogin(Data, Timestamp))
    break

  case DataEventEnum.LOGOUT:
    CapacitorService.isNative ? onLogout(Data, Timestamp) : OneSignal.push(() => onLogout(Data, Timestamp))
    break

  case DataEventEnum.ADD_TO_CART:
    CapacitorService.isNative ? onAddToCart(Data, Timestamp) : OneSignal.push(() => onAddToCart(Data, Timestamp))
    break

  case DataEventEnum.REMOVE_FROM_CART:
    CapacitorService.isNative ? onRemoveFromCart(Data, Timestamp) : OneSignal.push(() => onRemoveFromCart(Data, Timestamp))
    break

  case DataEventEnum.BEGIN_CHECKOUT:
    CapacitorService.isNative ? onBeginCheckout(Data, Timestamp) : OneSignal.push(() => onBeginCheckout(Data, Timestamp))
    break

  case DataEventEnum.PURCHASE:
    CapacitorService.isNative ? onPurchase(Data, Timestamp) : OneSignal.push(() => onPurchase(Data, Timestamp))
    break

  case DataEventEnum.ACCOUNT_UPDATE:
    CapacitorService.isNative ? onAccountUpdate(Data, Timestamp) : OneSignal.push(() => onAccountUpdate(Data, Timestamp))
    break

  case DataEventEnum.ACCOUNT_ENABLE:
    CapacitorService.isNative ? onAccountEnable(Data, Timestamp) : OneSignal.push(() => onAccountEnable(Data, Timestamp))
    break

  case DataEventEnum.ACCOUNT_DISABLE:
    CapacitorService.isNative ? onAccountDisable(Data, Timestamp) : OneSignal.push(() => onAccountDisable(Data, Timestamp))
    break

  case DataEventEnum.ACCOUNT_DELETE:
    CapacitorService.isNative ? onAccountDelete(Data, Timestamp) : OneSignal.push(() => onAccountDelete(Data, Timestamp))
    break

  case DataEventEnum.PUSH_NOTIFICATION_FOREGROUND_WILL_DISPLAY:
    CapacitorService.isNative ? onPushNotificationForegroundWillDisplay(Data, Timestamp) : OneSignal.push(() => onPushNotificationForegroundWillDisplay(Data, Timestamp))
    break

  case DataEventEnum.PUSH_NOTIFICATION_DISMISS:
    CapacitorService.isNative ? onPushNotificationDismiss(Data, Timestamp) : OneSignal.push(() => onPushNotificationDismiss(Data, Timestamp))
    break

  case DataEventEnum.PUSH_NOTIFICATION_PERMISSION_CHANGE:
    CapacitorService.isNative ? onPushNotificationPermissionChange(Data, Timestamp) : OneSignal.push(() => onPushNotificationPermissionChange(Data, Timestamp))
    break

  case DataEventEnum.PUSH_NOTIFICATION_PERMISSION_PROMPT_DISPLAY:
    CapacitorService.isNative ? onPushNotificationPermissionPromptDisplay(Data, Timestamp) : OneSignal.push(() => onPushNotificationPermissionPromptDisplay(Data, Timestamp))
    break

  case DataEventEnum.PUSH_NOTIFICATION_CLICK:
    CapacitorService.isNative ? onPushNotificationClick(Data, Timestamp) : OneSignal.push(() => onPushNotificationClick(Data, Timestamp))
    break

  case DataEventEnum.PUSH_SLIDE_DOWN_SHOWN:
    CapacitorService.isNative ? onPushSlideDownShown(Data, Timestamp) : OneSignal.push(() => onPushSlideDownShown(Data, Timestamp))
    break

  case DataEventEnum.PUSH_SLIDE_DOWN_ALLOW:
    CapacitorService.isNative ? onPushSlideDownAllow(Data, Timestamp) : OneSignal.push(() => onPushSlideDownAllow(Data, Timestamp))
    break

  case DataEventEnum.PUSH_SLIDE_DOWN_CANCEL:
    CapacitorService.isNative ? onPushSlideDownCancel(Data, Timestamp) : OneSignal.push(() => onPushSlideDownCancel(Data, Timestamp))
    break

  case DataEventEnum.PUSH_USER_SUBSCRIPTION_CHANGE:
    CapacitorService.isNative ? onPushUserSubscriptionChange(Data, Timestamp) : OneSignal.push(() => onPushUserSubscriptionChange(Data, Timestamp))
    break

  case DataEventEnum.PUSH_USER_STATE_CHANGE:
    CapacitorService.isNative ? onPushUserChange(Data, Timestamp) : OneSignal.push(() => onPushUserChange(Data, Timestamp))
    break
  }
}

const onInit = (Data) => {
  const OneSignal = window?.$OneSignal?.Service()
  if (!OneSignal) return

  const UserFirstname = Data.Payload?.Info?.Firstname || ''
  const Locale = Data.Payload?.Locale || 'en'

  OneSignal.User.setLanguage(Locale)

  if (Data.Payload.Info) {
    OneSignal.User.addTags({
      ...(tierTagAllowed(OSTagEnum.USER_TYPE) && {[OSTagEnum.USER_TYPE]: String(Data.Payload.Method)}),
      ...(tierTagAllowed(OSTagEnum.USER_LOCALE) && {[OSTagEnum.USER_LOCALE]: String(Data.Payload.Locale)}),
      ...(tierTagAllowed(OSTagEnum.FIRST_NAME) && {[OSTagEnum.FIRST_NAME]: String(UserFirstname)})
    })
  } else {
    OneSignal.User.addTags({
      ...(tierTagAllowed(OSTagEnum.USER_TYPE) && {[OSTagEnum.USER_TYPE]: String(Data.Payload.Method)}),
      ...(tierTagAllowed(OSTagEnum.USER_LOCALE) && {[OSTagEnum.USER_LOCALE]: String(Data.Payload.Locale)})
    })
  }

  setUserPreferences(Data)
}

const tierTagAllowed = (tag) => {
  return !!Tier[window.$OneSignal.Settings.Tier]?.Tags?.includes(tag)
}

const onLocale = (Data) => {
  const OneSignal = window?.$OneSignal?.Service()
  const Locale = Data?.locale || 'en'
  if (!OneSignal) return

  OneSignal.User.setLanguage(Locale || 'en')
}

const onLogin = (Data) => {
  const OneSignal = window?.$OneSignal?.Service()
  if (!OneSignal) return

  const User = Data.Payload?.User || null
  if (!User) return

  OneSignal.User.addTags({
    ...(tierTagAllowed(OSTagEnum.USER_TYPE) && {[OSTagEnum.USER_TYPE]: String(Data.Payload.Method)}),
    ...(tierTagAllowed(OSTagEnum.LOYALTY_POINTS) && {[OSTagEnum.LOYALTY_POINTS]: String(User?.Points || 0)}),
    ...(tierTagAllowed(OSTagEnum.FIRST_NAME) && {[OSTagEnum.FIRST_NAME]: String(User?.Firstname || '')})
  })

  setUserPreferences(User)
}

const onLogout = (Data) => {
  const OneSignal = window?.$OneSignal?.Service()
  if (!OneSignal) return

  OneSignal.logout()
}

const onAddToCart = (Data, Timestamp) => {
  const OneSignal = window?.$OneSignal?.Service()
  if (!OneSignal) return

  OneSignal.User.addTags({
    ...(tierTagAllowed(OSTagEnum.CART_UPDATE) && {[OSTagEnum.CART_UPDATE]: String(Timestamp)}),
    ...(tierTagAllowed(OSTagEnum.CART_ITEMS) && {[OSTagEnum.CART_ITEMS]: String(Data?.Payload?.cartTotalQuantity)})
  })
}

const onRemoveFromCart = (Data, Timestamp) => {
  const OneSignal = window?.$OneSignal?.Service()
  if (!OneSignal) return

  OneSignal.User.addTags({
    ...(tierTagAllowed(OSTagEnum.CART_UPDATE) && {[OSTagEnum.CART_UPDATE]: String(Timestamp)}),
    ...(tierTagAllowed(OSTagEnum.CART_ITEMS) && {[OSTagEnum.CART_ITEMS]: String(Data?.Payload?.cartTotalQuantity)})
  })
}

const onBeginCheckout = (Data, Timestamp) => {
  const OneSignal = window?.$OneSignal?.Service()
  if (!OneSignal) return

  OneSignal.User.addTags({
    ...(tierTagAllowed(OSTagEnum.CART_UPDATE) && {[OSTagEnum.CART_UPDATE]: String(Timestamp)}),
    ...(tierTagAllowed(OSTagEnum.CART_ITEMS) && {[OSTagEnum.CART_ITEMS]: String(Data?.Payload?.cartTotalQuantity)})
  })
}

const onPurchase = (Data, Timestamp) => {
  const OneSignal = window?.$OneSignal?.Service()
  if (!OneSignal) return

  OneSignal.User.getTags(tags => {
    let purchase_amount = 0
    if (tags[OSTagEnum.AMOUNT_SPENT]) purchase_amount = parseInt(tags[OSTagEnum.AMOUNT_SPENT]) || 0
    purchase_amount += parseInt(Data.Payload.value) || 0
    const UserFirstName = Data.Payload?.user?.firstname || ''

    OneSignal.User.addTags({
      ...(tierTagAllowed(OSTagEnum.LAST_ORDER) && {[OSTagEnum.LAST_ORDER]: String(Timestamp)}),
      ...(tierTagAllowed(OSTagEnum.AMOUNT_SPENT) && {[OSTagEnum.AMOUNT_SPENT]: String(purchase_amount)}),
      ...(tierTagAllowed(OSTagEnum.CART_UPDATE) && {[OSTagEnum.CART_UPDATE]: String('')}),
      ...(tierTagAllowed(OSTagEnum.CART_ITEMS) && {[OSTagEnum.CART_ITEMS]: String(0)}),
      ...(tierTagAllowed(OSTagEnum.FIRST_NAME) && {[OSTagEnum.FIRST_NAME]: String(UserFirstName)})
    })

    OneSignal.login(Data.Payload.user.email)
  })
}

const onAccountUpdate = (Data, Timestamp) => {
  setUserPreferences(Data)
}

const onAccountEnable = (Data, Timestamp) => {
  setUserPreferences(Data)
}

const onAccountDisable = (Data, Timestamp) => {
  clearUserPreferences(Data)
}

const onAccountDelete = (Data, Timestamp) => {
  clearUserPreferences(Data)
}

const onPushNotificationForegroundWillDisplay = (Data, Timestamp) => {

  console.log('onPushNotificationForegroundWillDisplay:', Data, Timestamp)
}
const onPushNotificationDismiss = (Data, Timestamp) => {

  console.log('onPushNotificationDismiss:', Data, Timestamp)
}
const onPushNotificationPermissionChange = (Data, Timestamp) => {

  console.log('onPushNotificationPermissionChange:', Data, Timestamp)
}
const onPushNotificationPermissionPromptDisplay = (Data, Timestamp) => {

  console.log('onPushNotificationPermissionPromptDisplay:', Data, Timestamp)
}
const onPushNotificationClick = (Data, Timestamp) => {

  console.log('onPushNotificationClick:', Data, Timestamp)
}
const onPushSlideDownShown = (Data, Timestamp) => {

  console.log('onPushSlideDownShown:', Data, Timestamp)
}
const onPushSlideDownAllow = (Data, Timestamp) => {

  console.log('onPushSlideDownAllow:', Data, Timestamp)
  updateCustomerWithUserNotificationTags()
}
const onPushSlideDownCancel = (Data, Timestamp) => {

  console.log('onPushSlideDownCancel:', Data, Timestamp)
}
const onPushUserSubscriptionChange = (Data, Timestamp) => {

  console.log('onPushUserSubscriptionChange:', Data, Timestamp)
}
const onPushUserChange = (Data, Timestamp) => {

  console.log('onPushUserChange:', Data, Timestamp)
}

const clearUserPreferences = (Data) => {
  const OneSignal = window?.$OneSignal?.Service()
  if (!OneSignal) return

  const UserEmail = Data.Payload?.Info?.Email || ''
  const UserMobile = Data.Payload?.Info?.Phone || ''

  if (UserEmail) OneSignal.User.removeEmail(UserEmail)
  if (UserMobile) OneSignal.User.removeSms(UserMobile)

  OneSignal.User.PushSubscription.optOut()
  OneSignal.logout()
}

const updateCustomerWithUserNotificationTags = () => {
  const OneSignal = window?.$OneSignal?.Service()
  if (!OneSignal || !window?.Vue) return

  const accountSettings = new AccountSettingsModel(window?.Vue?.user?.Info)
  if (accountSettings.Id) {
    setTimeout(async () => {
      const tags = OneSignal.User.getTags()
      accountSettings.Push = tags?.marketing === '1'
      accountSettings.PushSystem = tags?.system === '1'

      await accountSettings.SaveSettings(true)
        .then(response => {
          const data = API.responseData(response) || {}
          if (API.isResponseSuccess(response) && data?.CustomerInfo) {
            window.Vue.user = new CustomerModel(data.CustomerInfo)

            DataEventService.Emit(DataEventEnum.EVENT, {
              Event  : DataEventEnum.ACCOUNT_UPDATE,
              Payload: clone(window.Vue.user)
            })

            EventBus.$emit(DataEventEnum.ACCOUNT_UPDATE_PUSH, clone(window.Vue.user))
          }
        })
        .catch()
    }, 0)
  }
}

const setUserPreferences = (Data) => {
  const OneSignal = window?.$OneSignal?.Service()
  if (!OneSignal) return

  const Settings = window.$OneSignal.Settings
  const UserEmail = Data.Payload?.Info?.Email || ''
  const UserMobile = Data.Payload?.Info?.Phone || ''
  const PushMarketing = !Data?.Payload?.Info?.Push || !Data?.Payload?.Info?.Status ? '0' : Data?.Payload?.Info?.Push ? '1' : '0'
  const PushSystem = !Data?.Payload?.Info?.PushSystem || !Data?.Payload?.Info?.Status ? '0' : Data?.Payload?.Info?.PushSystem ? '1' : '0'
  const EmailMarketing = !Data?.Payload?.Info?.Newsletter || !Data?.Payload?.Info?.Status
  const SmsMarketing = !Data?.Payload?.Info?.SMS || !Data?.Payload?.Info?.Status

  if (Settings.Push.Active) {
    if (UserEmail) OneSignal.login(UserEmail)

    const tags = OneSignal.User.getTags()

    if (tags?.marketing !== PushMarketing) OneSignal.User.addTags({[OSTagEnum.NOTIFICATIONS_MARKETING]: PushMarketing})
    if (tags?.system !== PushSystem) OneSignal.User.addTags({[OSTagEnum.NOTIFICATIONS_SYSTEM]: PushSystem})

    if (tags?.marketing !== PushMarketing || tags?.system !== PushSystem) {
      if (PushMarketing === '0' && PushSystem === '0') OneSignal.User.PushSubscription.optOut()
      if (PushMarketing === '1' || PushSystem === '1') OneSignal.User.PushSubscription.optIn()
    }
  }

  if (Settings.Email.Active) EmailMarketing ? OneSignal.User.removeEmail(UserEmail) : OneSignal.User.addEmail(UserEmail)
  if (Settings.Sms.Active) SmsMarketing ? OneSignal.User.removeSms(UserMobile) : OneSignal.User.addSms(UserMobile)
}
